import React from 'react';
import {Box, Typography} from '@mui/material';
import type {Theme} from '@mui/material/styles';
import {
  PickersLayoutContentWrapper,
  PickersLayoutProps,
  PickersLayoutRoot,
  pickersLayoutClasses,
  usePickerLayout,
} from '@mui/x-date-pickers/PickersLayout';
import type {DateView} from '@mui/x-date-pickers/models';
import type {Moment} from 'moment';
import {AVAILABILITY_COLORS, AvailableDot, SpacesLeftPill} from './AvailabilityPickersDay';

const Swatch = ({sx}: {sx: object}) => (
  <Box component="span" sx={{display: 'inline-block', width: 18, height: 18, borderRadius: '50%', boxSizing: 'border-box', ...sx}}/>
);

const LEGEND_ITEMS: {key: string; marker: React.ReactNode; label: string}[] = [
  {key: 'available', marker: <AvailableDot/>, label: 'Available'},
  {key: 'spaces-left', marker: <SpacesLeftPill>#</SpacesLeftPill>, label: 'Spaces left'},
  {
    key: 'full',
    marker: <Swatch sx={{backgroundColor: AVAILABILITY_COLORS.fullFill, border: '1px solid #e0e0e0'}}/>,
    label: 'Full',
  },
  {
    key: 'today',
    marker: <Swatch sx={{boxShadow: (theme: Theme) => `inset 0 0 0 2px ${theme.palette.primary.main}`}}/>,
    label: 'Today',
  },
  {key: 'selected', marker: <Swatch sx={{backgroundColor: AVAILABILITY_COLORS.selected}}/>, label: 'Selected'},
];

export const AvailabilityLegend = () => (
  <Box
    component="ul"
    aria-label="Calendar legend"
    sx={{
      display: 'flex',
      flexWrap: 'wrap',
      justifyContent: 'center',
      gap: '8px 18px',
      listStyle: 'none',
      m: 0,
      mx: 2,
      mb: 1.5,
      px: 0,
      pt: 1.25,
      borderTop: '1px solid #eceef5',
    }}
  >
    {LEGEND_ITEMS.map(({key, marker, label}) => (
      <Box component="li" key={key} sx={{display: 'flex', alignItems: 'center', gap: 0.75}}>
        <Box component="span" sx={{display: 'inline-flex', alignItems: 'center', justifyContent: 'center', minWidth: 20}}>
          {marker}
        </Box>
        <Typography component="span" variant="caption" sx={{color: 'text.secondary', fontSize: {xs: '0.8rem', sm: '0.875rem'}}}>
          {label}
        </Typography>
      </Box>
    ))}
  </Box>
);

export interface AvailabilityPickersLayoutProps extends PickersLayoutProps<Moment | null, Moment, DateView> {
  /** Says which date the calendar picks, e.g. "Select start date". */
  heading?: string;
  subheading?: string;
}

/** The default picker layout with a heading above and the availability legend below the day calendar. */
const AvailabilityPickersLayout = (props: AvailabilityPickersLayoutProps) => {
  const {toolbar, tabs, content, actionBar, shortcuts} = usePickerLayout(props);
  const {sx, className, view, heading, subheading, wrapperVariant} = props;

  return (
    <PickersLayoutRoot ownerState={props} sx={sx} className={[pickersLayoutClasses.root, className].filter(Boolean).join(' ')}>
      {toolbar}
      {shortcuts}
      <PickersLayoutContentWrapper className={pickersLayoutClasses.contentWrapper}>
        {/* The mobile toolbar already shows the heading as its title. */}
        {heading && wrapperVariant !== 'mobile' && (
          <Box sx={{px: 3, pt: 2, pb: 0.5}}>
            <Typography component="h2" sx={{fontSize: '1.05rem', fontWeight: 600, color: 'primary.main'}}>
              {heading}
            </Typography>
            {subheading && (
              <Typography variant="body2" sx={{color: 'text.secondary'}}>{subheading}</Typography>
            )}
          </Box>
        )}
        {tabs}
        {content}
        {view === 'day' && <AvailabilityLegend/>}
      </PickersLayoutContentWrapper>
      {actionBar}
    </PickersLayoutRoot>
  );
};

export default AvailabilityPickersLayout;
